import {
  MeetingIntentDetectedEventSchema,
  type MeetingIntentDetectedPayload,
  type MeetingIntentOverlayResolution,
} from '@electron/shared/meetings.types';
import { EventNames, globalEventBus } from '@littlebird/event-bus';
import { config, resolveMeetingIntelligenceEnabled } from '@littlebird/sdk';
import { useEffect } from 'react';

import { contextChatStore } from '@/features/context-chat';
import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';
import { trackEvent } from '@/utils/analytics';
import { navigate } from '@/utils/navigation-service';

import { meetingStore } from './meetings.state';

function analyticsProperties(intent: MeetingIntentDetectedPayload) {
  return {
    trigger: intent.trigger,
    stream: intent.stream,
    confidence: intent.confidence,
    meeting_id: intent.meetingId,
  };
}

function handleResolution(resolution: MeetingIntentOverlayResolution) {
  trackEvent('MEETING_INTENT_PROMPT_RESOLVED', {
    ...analyticsProperties(resolution),
    outcome: resolution.outcome,
  });

  if (resolution.outcome !== 'approved') return;

  electronApis.window.focusMainWindow();
  navigate({ to: `/meetings/${resolution.meetingId}` });
  contextChatStore.askMeetingIntent(
    { type: 'meeting', meetingId: resolution.meetingId },
    resolution.prompt
  );
}

export function useMeetingIntentPrompt() {
  useEffect(() => {
    const subscription = globalEventBus.on(
      EventNames.MEETING_INTENT_DETECTED,
      ({ data }) => {
        const parsedEvent = MeetingIntentDetectedEventSchema.safeParse(data);
        if (!parsedEvent.success) {
          logError(parsedEvent.error, 'Invalid meeting intent event');
          return;
        }
        const intent = parsedEvent.data;
        if (!meetingStore.isMeetingIdRecording(intent.meetingId)) return;
        const meetingIntelligenceOn = resolveMeetingIntelligenceEnabled(
          config.meetings.meeting_intelligence_enabled
        );
        if (!meetingIntelligenceOn) return;
        void electronApis.meetingIntentOverlay.show(intent).catch((error) => {
          logError(error, 'Failed to show meeting intent prompt');
        });
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  useEffect(
    () =>
      electronApis.meetingIntentOverlay.listenPresented((intent) => {
        trackEvent('MEETING_INTENT_PROMPT_SHOWN', analyticsProperties(intent));
      }),
    []
  );

  useEffect(
    () =>
      electronApis.meetingIntentOverlay.listenResolution((resolution) => {
        // A listener whose callback throws is removed, which would end approvals until restart
        try {
          handleResolution(resolution);
        } catch (error) {
          logError(error, 'Failed to handle meeting intent resolution');
        }
      }),
    []
  );
}
